/**
 * Composed Loading Skeletons
 * Reusable skeleton patterns for common UI components
 *
 * Created: 2026-01-29 - Fas 4 Performance (Quick Win #3)
 */

import { Skeleton } from './skeleton'

/**
 * List item skeleton with icon, title, and optional subtitle
 */
export function ListItemSkeleton() {
  return (
    <div className="flex items-start gap-3 p-3">
      {/* Icon */}
      <Skeleton className="h-5 w-5 shrink-0 rounded" />

      {/* Content */}
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-3 w-1/2" />
      </div>
    </div>
  )
}

/**
 * Multiple list items skeleton
 */
export function ListSkeleton({ count = 5 }: { count?: number }) {
  return (
    <div className="space-y-1">
      {Array.from({ length: count }).map((_, i) => (
        <ListItemSkeleton key={i} />
      ))}
    </div>
  )
}

/**
 * Card skeleton with header and content
 */
export function CardSkeleton() {
  return (
    <div className="rounded-lg border bg-card p-6 space-y-4">
      {/* Header */}
      <div className="space-y-2">
        <Skeleton className="h-5 w-1/3" />
        <Skeleton className="h-4 w-2/3" />
      </div>

      {/* Content */}
      <div className="space-y-2">
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-5/6" />
        <Skeleton className="h-3 w-4/6" />
      </div>
    </div>
  )
}

/**
 * Table row skeleton
 */
export function TableRowSkeleton() {
  return (
    <div className="flex items-center gap-4 p-4 border-b">
      <Skeleton className="h-4 w-4 rounded" /> {/* Checkbox */}
      <Skeleton className="h-4 w-1/4" /> {/* Title */}
      <Skeleton className="h-4 w-1/6" /> {/* Status */}
      <Skeleton className="h-4 w-1/6" /> {/* Date */}
      <Skeleton className="h-4 w-1/6" /> {/* Tags */}
    </div>
  )
}

/**
 * Table skeleton
 */
export function TableSkeleton({ rows = 10 }: { rows?: number }) {
  return (
    <div className="border rounded-md">
      {/* Header */}
      <div className="flex items-center gap-4 p-4 border-b bg-muted/30">
        <Skeleton className="h-4 w-4 rounded" />
        <Skeleton className="h-4 w-1/4" />
        <Skeleton className="h-4 w-1/6" />
        <Skeleton className="h-4 w-1/6" />
        <Skeleton className="h-4 w-1/6" />
      </div>

      {/* Rows */}
      {Array.from({ length: rows }).map((_, i) => (
        <TableRowSkeleton key={i} />
      ))}
    </div>
  )
}

/**
 * Activity/Timeline item skeleton
 */
export function ActivityItemSkeleton() {
  return (
    <div className="flex gap-4 p-4">
      {/* Avatar/Icon */}
      <Skeleton className="h-8 w-8 rounded-full shrink-0" />

      {/* Content */}
      <div className="flex-1 space-y-2">
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-3 w-20" />
        </div>
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-3/4" />
      </div>
    </div>
  )
}

/**
 * Activity list skeleton
 */
export function ActivityListSkeleton({ count = 5 }: { count?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: count }).map((_, i) => (
        <ActivityItemSkeleton key={i} />
      ))}
    </div>
  )
}

/**
 * Breadcrumb skeleton
 */
export function BreadcrumbSkeleton() {
  return (
    <div className="flex items-center gap-2">
      <Skeleton className="h-4 w-16" />
      <span className="text-muted-foreground">/</span>
      <Skeleton className="h-4 w-20" />
      <span className="text-muted-foreground">/</span>
      <Skeleton className="h-4 w-24" />
    </div>
  )
}

/**
 * Settings section skeleton
 */
export function SettingsSkeleton() {
  return (
    <div className="space-y-6">
      {/* Section 1 */}
      <div className="space-y-4">
        <Skeleton className="h-6 w-40" /> {/* Section title */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-48" />
            </div>
            <Skeleton className="h-9 w-16 rounded-full" /> {/* Switch */}
          </div>
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-3 w-56" />
            </div>
            <Skeleton className="h-9 w-16 rounded-full" />
          </div>
        </div>
      </div>

      {/* Section 2 */}
      <div className="space-y-4">
        <Skeleton className="h-6 w-32" />
        <div className="space-y-3">
          <div className="space-y-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-10 w-full rounded-md" /> {/* Input */}
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-10 w-full rounded-md" />
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Notification item skeleton
 */
export function NotificationItemSkeleton() {
  return (
    <div className="flex gap-3 p-4 border-b">
      <Skeleton className="h-2 w-2 rounded-full shrink-0 mt-2" /> {/* Unread dot */}

      <div className="flex-1 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-16" /> {/* Time */}
        </div>
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-2/3" />
      </div>
    </div>
  )
}

/**
 * Notification list skeleton
 */
export function NotificationListSkeleton({ count = 5 }: { count?: number }) {
  return (
    <div>
      {Array.from({ length: count }).map((_, i) => (
        <NotificationItemSkeleton key={i} />
      ))}
    </div>
  )
}

/**
 * Page header skeleton (for PageView, etc.)
 */
export function PageHeaderSkeleton() {
  return (
    <div className="space-y-4 pb-6 border-b">
      {/* Breadcrumb */}
      <BreadcrumbSkeleton />

      {/* Title area */}
      <div className="flex items-start gap-3">
        <Skeleton className="h-8 w-8 rounded shrink-0" /> {/* Icon */}
        <div className="flex-1 space-y-2">
          <Skeleton className="h-8 w-1/2" /> {/* Title */}
          <div className="flex items-center gap-2">
            <Skeleton className="h-6 w-20 rounded-full" /> {/* Badge */}
            <Skeleton className="h-6 w-24 rounded-full" /> {/* Badge */}
          </div>
        </div>
      </div>
    </div>
  )
}
